<template>
  <div class="col-xs-12">
    <steps :filled-steps="filledSteps" />

    <h2 class="title-2 text-center partner-title">
      Teacher Information
    </h2>

    <form @submit.prevent="handleSubmit" class="form-container" novalidate>
      <p class="description">
        Please continue with your application
      </p>

      <div class="grid">
        <div class="grid-1">
          <div class="input-group">
            <label for="">Name</label>
            <input type="text" name="name" v-model="form.name" class="input-field success" required disabled>
            <span class="span-placeholder">
              <i class="fal fa-user" aria-hidden="true"></i>
            </span>
            <span class="span-state success">
              <i class="fal fa-check" aria-hidden="true"></i>
            </span>
          </div>

          <div class="input-group">
            <label for="">Email</label>
            <input type="email" v-model="form.email" class="input-field success" required disabled>
            <span class="span-placeholder">
              <i class="fal fa-envelope" aria-hidden="true"></i>
            </span>
            <span class="span-state success">
              <i class="fal fa-check" aria-hidden="true"></i>
            </span>
          </div>

          <div class="input-group">
            <label for="">Password</label>
            <input
              :type="computedPasswordType"
              v-model="form.password"
              class="input-field"
              placeholder="***********"
              @input="$v.form.password.$touch()"
              :class="{ 'success': !$v.form.password.$invalid, 'warning': $v.form.password.$error  }"
            >
            <span class="span-placeholder">
              <i class="fal fa-lock-alt" aria-hidden="true"></i>
            </span>
            <span v-show="!$v.form.password.$invalid" class="span-state success">
              <i class="fal fa-check" aria-hidden="true"></i>
            </span>
            <span class="span-password">
              <button type="button" @click="handleBandShowPassword" v-show="!bandShowPassword">
                <i
                  class="fas fa-eye"
                />
              </button>
              <button type="button" @click="handleBandShowPassword" v-show="bandShowPassword">
                <i
                  class="fas fa-eye-slash"
                />
              </button>
            </span>

            <transition name="fade">
              <warning-alert v-if="$v.form.password.$error">
                This is not a valid password, minimum length should be 6
              </warning-alert>
            </transition>
          </div>

          <div class="input-group">
            <label for="">Phone Number</label>
            <input
              type="tel"
              v-model="form.phoneNumber"
              class="input-field"
              placeholder="555-111-1111"
              @input="$v.form.phoneNumber.$touch()"
              :class="{ 'success': !$v.form.phoneNumber.$invalid, 'warning': $v.form.phoneNumber.$error  }"
              required
            >
            <span class="span-placeholder">
              <i class="fal fa-mobile" aria-hidden="true"></i>
            </span>
            <span v-show="!$v.form.phoneNumber.$invalid" class="span-state">
              <i class="fal fa-check" aria-hidden="true"></i>
            </span>

            <transition name="fade">
              <warning-alert v-if="$v.form.phoneNumber.$error">
                This is not a valid number!
              </warning-alert>
            </transition>
          </div>

          <div class="input-group">
            <label for="">Personal Address (Line 1)</label>
            <input
              type="text"
              v-model="form.addressOne"
              class="input-field"
              placeholder="Adress line 1"
              @input="$v.form.addressOne.$touch()"
              :class="{ 'success': !$v.form.addressOne.$invalid, 'warning': $v.form.addressOne.$error  }"
              required
            >
            <span class="span-placeholder">
              <i class="fal fa-home" aria-hidden="true"></i>
            </span>
            <span v-show="!$v.form.addressOne.$invalid" class="span-state">
              <i class="fal fa-check" aria-hidden="true"></i>
            </span>

            <transition name="fade">
              <warning-alert v-if="$v.form.addressOne.$error">
                This field is required!
              </warning-alert>
            </transition>
          </div>

          <div class="input-group">
            <label for="">Personal Address (Line 2)</label>
            <input type="text" v-model="form.addressTwo" class="input-field" placeholder="Adress line 2">
            <span class="span-placeholder">
              <i class="fal fa-home" aria-hidden="true"></i>
            </span>
          </div>

          <div class="input-group">
            <label for="">City</label>
            <input
              type="text"
              v-model="form.city"
              class="input-field"
              placeholder="City"
              @input="$v.form.city.$touch()"
              :class="{ 'success': !$v.form.city.$invalid, 'warning': $v.form.city.$error  }"
              required
            >
            <span class="span-placeholder">
              <i class="far fa-building" aria-hidden="true"></i>
            </span>
            <span v-show="!$v.form.city.$invalid" class="span-state">
              <i class="fal fa-check" aria-hidden="true"></i>
            </span>

            <transition name="fade">
              <warning-alert v-if="$v.form.city.$error">
                This field is required!
              </warning-alert>
            </transition>
          </div>
        </div>

        <div class="grid-2">
          <div class="input-group">
            <label for="">State</label>
            <select
              v-model="form.state"
              class="input-field"
              @change="$v.form.state.$touch()"
              :class="{
                'success': !$v.form.state.$invalid && $v.form.state.$dirty,
                'warning': $v.form.state.$invalid && $v.form.state.$dirty
              }"
              required
            >
              <option :value="null">
                State
              </option>
              <option
                v-for="(state, index) in states"
                :value="state.id"
                :key="index"
              >
                {{state.name}}
              </option>
            </select>
            <span class="span-placeholder">
              <i class="fal fa-city" aria-hidden="true"></i>
            </span>
            <span
              v-if="!$v.form.state.$invalid && $v.form.state.$dirty"
              class="span-state success"
            >
              <i class="fal fa-check" aria-hidden="true"></i>
            </span>
            <transition name="fade">
              <warning-alert v-if="$v.form.state.$error">
                This field is required!
              </warning-alert>
            </transition>
          </div>

          <div class="input-group">
            <label for="">Zip Code</label>
            <select
              v-model="form.zipcode"
              class="input-field"
              @change="$v.form.zipcode.$touch()"
              :class="{
                'success': !$v.form.zipcode.$invalid && $v.form.zipcode.$dirty,
                'warning': $v.form.zipcode.$invalid && $v.form.zipcode.$dirty
              }"
              required
            >
              <option :value="null">
                Zip Code
              </option>
              <option
                v-for="(zipcode, index) in computedZipCodes"
                :value="zipcode.zipcode"
                :key="index"
              >
                {{zipcode.zipcode}}
              </option>
            </select>
            <span class="span-placeholder">
              <i class="fal fa-map-pin" aria-hidden="true"></i>
            </span>
            <span
              v-if="!$v.form.zipcode.$invalid && $v.form.zipcode.$dirty"
              class="span-state success"
            >
              <i class="fal fa-check" aria-hidden="true"></i>
            </span>
            <transition name="fade">
              <warning-alert v-if="$v.form.zipcode.$error">
                This field is required!
              </warning-alert>
            </transition>
          </div>

          <div class="input-group">
            <p class="text-center paragraph">
              Have you shopped at a <b>KINF Affiliated Teacher Resource Center</b> in the last <b>12 months</b>?
            </p>

            <div class="d-block mx-auto mb-0">
              <div class="input-radio">
                <input type="radio" :value="true" v-model="form.isAffiliatedLastYear">
                <span>
                  Yes
                </span>
                <!-- <span class="inner-circle" :class="{ 'filled-background': form.isAffiliatedLastYear }"></span> -->
              </div>
              <div class="input-radio">
                <input type="radio" :value="false" v-model="form.isAffiliatedLastYear">
                <span>
                  No
                </span>
                <!-- <span class="inner-circle" :class="{ 'filled-background': !form.isAffiliatedLastYear }"></span> -->
              </div>

              <p class="paragraph mb-0 mt-2" style="margin-top: 1rem;">
                If yes above, please select the Teacher Resource Center
                <button type="button" style="background: transparent;" @click="handleResourceCenterAlert()">
                  <i class="far fa-question-circle" aria-hidden="true" />
                </button>
              </p>
            </div>

            <div class="input-group">
              <label for="">&nbsp;</label>
              <select
                v-model="form.teacherResourceCenter"
                :disabled="!form.isAffiliatedLastYear"
                class="input-field"
              >
                <option :value="null">
                  Select a Resource Center
                </option>
                <option
                  v-for="center in centers"
                  :key="center.id"
                  :value="center.id"
                >
                  {{center.name}} - {{center.city}}, {{center.state.slug}}
                </option>
              </select>
              <span class="span-placeholder">
                <i class="fal fa-school" aria-hidden="true"></i>
              </span>
            </div>

            <transition name="fade">
              <div class="resource-center-alert" v-if="bandResourceCenterAlert">
                <p>
                  Check if you shopped in a Resource Center Before:
                </p>
                <a href="https://www.kinf.org/programs/network/" target="_blank" rel="noopener noreferrer">
                  https://www.kinf.org/programs/network/
                </a>
              </div>
            </transition>
          </div>

          <p class="paragraph mb-0">
            Please choose the best option for the grade that you teach
          </p>

          <div class="input-group">
            <label for="">&nbsp;</label>
            <select
              v-model="form.grade"
              class="input-field"
              @change="$v.form.grade.$touch()"
              :class="{
                'success': !$v.form.grade.$invalid && $v.form.grade.$dirty,
                'warning': $v.form.grade.$invalid && $v.form.grade.$dirty
              }"
              required
            >
              <option :value="null">Grade</option>
              <option
                v-for="(grade, index) in grades"
                :key="index"
                :value="grade.id"
              >
                {{ grade.name }}
              </option>
            </select>
            <span class="span-placeholder">
              <i class="fal fa-graduation-cap" aria-hidden="true"></i>
            </span>
            <span
              v-if="!$v.form.grade.$invalid && $v.form.grade.$dirty"
              class="span-state success"
            >
              <i class="fal fa-check" aria-hidden="true"></i>
            </span>
            <transition name="fade">
              <warning-alert v-if="$v.form.grade.$error">
                This field is required!
              </warning-alert>
            </transition>
          </div>

          <div class="input-group">
            <p class="text-center paragraph">
              Have you applied for a supply box from KINF in prior years?
            </p>

            <div class="d-block mx-auto">
              <div class="input-radio">
                <input type="radio" :value="true" v-model="form.isSupplyBoxFromKinf">
                <span>Yes</span>
                <span class="inner-circle" :class="{ 'filled-background': form.isSupplyBoxFromKinf }"></span>
              </div>
              <div class="input-radio">
                <input type="radio" :value="false" v-model="form.isSupplyBoxFromKinf">
                <span>No</span>
                <span class="inner-circle" :class="{ 'filled-background': !form.isSupplyBoxFromKinf }"></span>
              </div>
            </div>
          </div>

          <button type="submit" class="btn btn-teacher-form" :disabled="isSubmitting">
            <span v-show="!isSubmitting">
              CONTINUE
            </span>
            <span v-show="isSubmitting">
              <i class="fas fa-spin fa-spinner" />
              PROCESSING
            </span>
          </button>
        </div>
      </div>
    </form>
  </div>
</template>

<script>
import Steps from './Steps.vue'
import { required, email, minLength, helpers } from 'vuelidate/lib/validators'
import TeacherService from '../../../services/TeacherService'
import WarningAlert from '../components/WarningAlert'

const phoneValidation = helpers.regex('phoneValidation', /^\(?([0-9]{3})\)?[-. ]?([0-9]{3})[-. ]?([0-9]{4})$/)

export default {
  name: 'TeacherInformation',
  components: {
    Steps,
    WarningAlert
  },
  props: {
    teacher: Object,
    grades: Array,
    zipCodes: Array,
    centers: Array,
    states: Array,
    filledSteps: Array
  },
  data () {
    return {
      bandResourceCenterAlert: false,
      bandShowPassword: false,
      isSubmitting: false,
      form: {
        name: this.teacher.name,
        email: this.teacher.email,
        password: null,
        phoneNumber: null,
        addressOne: null,
        addressTwo: null,
        city: null,
        isAffiliatedLastYear: false,
        isSupplyBoxFromKinf: false,
        state: null,
        zipcode: null,
        grade: null,
        teacherResourceCenter: null
      }
    }
  },
  created () {
    if (this.teacher.teacher) {
      this.form.phoneNumber = this.teacher.teacher.teacher_data.personal_information.phoneNumber
      this.form.addressOne = this.teacher.teacher.teacher_data.personal_information.addressOne
      this.form.addressTwo = this.teacher.teacher.teacher_data.personal_information.addressTwo
      this.form.city = this.teacher.teacher.teacher_data.personal_information.city
      this.form.isAffiliatedLastYear = this.teacher.teacher.teacher_data.personal_information.isAffiliatedLastYear
      this.form.isSupplyBoxFromKinf = this.teacher.teacher.teacher_data.personal_information.isSupplyBoxFromKinf
      this.form.state = this.teacher.teacher.state_id
      this.form.zipcode = this.teacher.teacher.teacher_data.personal_information.zipcode
      this.form.grade = this.teacher.teacher.grade_id
      this.form.teacherResourceCenter = this.teacher.teacher.teacher_data.personal_information.teacherResourceCenter
    }
  },
  computed: {
    computedZipCodes () {
      const selectedState = this.states.find((state) => state.id === this.form.state)

      if (selectedState === undefined) {
        return []
      }

      return this.zipCodes.filter((zipcode) => zipcode.state === selectedState.slug)
    },
    computedPasswordType () {
      return this.bandShowPassword ? 'text' : 'password'
    }
  },
  methods: {
    handleResourceCenterAlert () {
      this.bandResourceCenterAlert = !this.bandResourceCenterAlert
    },
    handleBandShowPassword () {
      this.bandShowPassword = !this.bandShowPassword
    },
    async handleSubmit () {
      this.touchForm()

      if (!this.$v.form.$anyError && this.$v.form.$anyDirty && !this.isSubmitting) {
        this.isSubmitting = true
        const service = new TeacherService()
        service.save(this.form)
      }
    },
    touchForm () {
      this.$v.form.name.$touch()
      this.$v.form.email.$touch()
      this.$v.form.grade.$touch()
      this.$v.form.zipcode.$touch()
      this.$v.form.state.$touch()
      this.$v.form.city.$touch()
      this.$v.form.phoneNumber.$touch()
    }
  },
  validations: {
    form: {
      name: {
        required
      },
      email: {
        required,
        email
      },
      password: {
        required,
        minLength: minLength(6)
      },
      phoneNumber: {
        required,
        phoneValidation
      },
      addressOne: {
        required
      },
      city: {
        required
      },
      state: {
        required
      },
      zipcode: {
        required
      },
      grade: {
        required
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.partner-title {
  color: #6D6E71;
  font-size: 30px;
}

.description {
  font-family: 'Roboto Slab';
  font-weight: bold;
  font-size: 22px;
  color: #999999;
  margin-bottom: 1.5rem;
}

.form-container {
  max-width: 750px;
  margin: 0 auto;
}

.grid {
  display: grid;
  grid-template-columns: 1fr;

  .input-group {
    margin-bottom: 20px;
    display: block;

    label {
      margin-bottom: 10px;
      font-family: 'Rubik';
      font-weight: 400;
      font-size: 18px;
      color: #6d6e71;
    }

    .input-field {
      font-weight: 300;
      font-size: 16px;
      font-family: 'Rubik';
      background-color: #F0F0F0;
      border-color: #6D6E71;
      border-width: 1px;

      box-sizing: border-box;
      padding-left: 45px;

      &:not(.no-fa-icon) {
        -webkit-appearance: none;
        -moz-appearance: none;
        appearance: none;
      }

    }

    .input-field.success {
      border-color: #60B661;
    }

    .span-placeholder {
      position: absolute;
      top: 50px;
      left: 20px;
      font-size: 16px;
      color: #999999;
    }

    .span-state {
      position: absolute;
      top: 50px;
      right: 20px;
      font-size: 16px;
    }

    .input-radio {
      display: inline;
      margin-right: 45px;
      // margin-bottom: 10px;
      font-family: 'Rubik';
      font-weight: 400;
      font-size: 18px;
      color: #6d6e71;
      position: relative;
    }

    .d-block.mx-auto {
      display: block;
      margin: 0 auto;
      margin-bottom: 30px;
      text-align: center;
    }

    input[type=radio] {
      margin: 0 auto;
      border: 4px solid #6D6E71;
      width: 22px;
      height: 22px;
      border-radius: 50%;
      transition: all ease .2s;

      &:hover {
        cursor: pointer;
      }

      &:checked {
        background-color: #FE8304;
        box-shadow: inset 0px 0px 0px 3px white;
        border: 4px solid #FE8304;
      }
    }
  }

  .span-password {
    position: absolute;
    top: 52px;
    right: 55px;

    button {
      color: #999;
      font-size: 16px;
      line-height: 19px;
      background-color: transparent;
    }
  }

  @media(min-width: 768px) {
    grid-template-columns: 1fr 1fr;
    gap: 45px;
  }
}

.resource-center-alert {
  border: 1px solid #DDC5A4;
  display: block;
  width: 100%;
  padding: 1rem;
  background-color: #FFF4E5;
  border-radius: 30px;
  margin-bottom: 2rem;

  font-family: 'Rubik';
  font-weight: 300;
  color: #663C00;
  font-size: 14px;
  line-height: 17px;

  p {
    margin-bottom: 1rem;
  }

  a  {
    color: #663C00;
    text-decoration: underline;
  }
}

.btn-teacher-form {
  width: 100%;
  max-width: 410px;
  margin: 0 auto;
  display: block;
  padding: .75rem;
  border: 2px solid #FD8204;
  color: #FD8204;
  font-size: 20px;
  font-weight: 500;
  background-color: #FFF;
  border-radius: 10px;
  font-family: 'Nunito';
}

.mb-0 {
  margin-bottom: 0;
}

// .inner-circle {
//   display: block;
//   width: 8px;
//   height: 8px;
//   background: transparent;
//   border-radius: 10px;
//   position: absolute;
//   top: 2.5px;
//   left: 7px;
//   z-index: -99;
//   transition: all ease .2s;
// }

.filled-background {
  background: #FE8304;
}
</style>



// WEBPACK FOOTER //
// resources/js/views/TeachersForm/Forms/TeacherInformation.vue